import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'login' | 'register'>(
    searchParams.get('tab') === 'register' ? 'register' : 'login'
  );
  const [loginForm, setLoginForm] = useState({ email: '', password: '', remember: false });
  const [regForm, setRegForm] = useState({ name: '', email: '', phone: '+44 ', school: '', yearGroup: '', password: '', role: 'student' as 'student' | 'tutor' });
  const [showPass, setShowPass] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ show: boolean, message: string, type: 'success' | 'error' }>({ show: false, message: '', type: 'success' });
  const navigate = useNavigate();

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  useEffect(() => {
    setActiveTab(searchParams.get('tab') === 'register' ? 'register' : 'login');
  }, [searchParams]);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginForm.email, password: loginForm.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      // store token and user
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setSubmitted(true);
      showToast('Welcome back! You have successfully logged in.', 'success');
      // role-based redirect with delay for success message
      setTimeout(() => {
        const role = data.user?.role;
        if (role === 'admin') navigate('/admin/dashboard', { replace: true });
        else if (role === 'tutor') navigate('/tutor/dashboard', { replace: true });
        else navigate('/student/dashboard', { replace: true });
      }, 2000);
    } catch (err: any) {
      const msg = err.message || 'Network error';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      setSubmitted(true);
      showToast('Account created successfully! Welcome to GCSE Computing.', 'success');
      // After registering, show success message then switch to login tab
      setTimeout(() => {
        setSubmitted(false);
        setActiveTab('login');
        navigate('/login?tab=login&registered=1', { replace: true });
      }, 2000);
    } catch (err: any) {
      const msg = err.message || 'Network error';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f8fafc' }}>
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-24">
        <div className="w-full max-w-5xl flex flex-col lg:flex-row rounded-2xl overflow-hidden border border-slate-100 bg-white">
          {/* Left Panel */}
          <div className="relative lg:w-5/12 bg-slate-900 p-10 flex flex-col justify-between min-h-64 lg:min-h-0">
            <div className="absolute inset-0">
              <img src="https://readdy.ai/api/search-image?query=abstract%20dark%20navy%20technology%20background%20with%20subtle%20glowing%20circuit%20patterns%20and%20geometric%20shapes%2C%20deep%20professional%20atmosphere%2C%20no%20people%2C%20clean%20minimal%20design%20with%20blue%20accent%20lighting&width=600&height=800&seq=loginbg1&orientation=portrait" alt="" className="w-full h-full object-cover object-top opacity-30" />
              <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900/95"></div>
            </div>
            <div className="relative">
              <Link to="/" className="flex items-center gap-2 mb-10 group">
                <img src="/logo.png" alt="GCSE Computing Logo" className="h-10 w-auto object-contain" />
                <span className="font-bold text-lg text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
                  GCSE <span className="text-emerald-400">Computing</span>
                </span>
              </Link>
              <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                Your path to a Grade 9 starts here.
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-8">
                Join 12,000+ UK students revising smarter with syllabus-aligned resources for AQA, OCR, Edexcel and WJEC.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  { icon: 'ri-book-open-line', text: 'Full syllabus coverage for all 4 exam boards' },
                  { icon: 'ri-file-text-line', text: 'Past papers, notes & practice questions' },
                  { icon: 'ri-calendar-event-line', text: 'Live revision sessions with expert tutors' },
                  { icon: 'ri-bar-chart-line', text: 'Track your progress topic by topic' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-emerald-600/20 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-emerald-400 text-sm`}></i>
                    </div>
                    <p className="text-slate-300 text-sm">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative mt-8 pt-6 border-t border-slate-700">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[
                    'https://readdy.ai/api/search-image?query=portrait%20smiling%20teenage%20student%20school%20uniform%20white%20background%20headshot&width=40&height=40&seq=av1&orientation=squarish',
                    'https://readdy.ai/api/search-image?query=portrait%20smiling%20teenage%20boy%20casual%20clothes%20white%20background%20headshot&width=40&height=40&seq=av2&orientation=squarish',
                    'https://readdy.ai/api/search-image?query=portrait%20smiling%20young%20female%20student%20white%20background%20headshot&width=40&height=40&seq=av3&orientation=squarish',
                  ].map((src, i) => (
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-slate-900 overflow-hidden flex-shrink-0">
                      <img src={src} alt="" className="w-full h-full object-cover object-top" />
                    </div>
                  ))}
                </div>
                <p className="text-slate-400 text-xs">Joined by <strong className="text-white">12,000+</strong> students this year</p>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="flex-1 p-8 lg:p-12">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-2xl mb-4">
                  <i className="ri-check-line text-emerald-600 text-3xl"></i>
                </div>
                <h3 className="text-slate-800 font-bold text-xl mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {activeTab === 'login' ? 'Welcome back!' : 'Account Created!'}
                </h3>
                <p className="text-slate-500 text-sm mb-6">
                  {activeTab === 'login' ? "You're now logged in to GCSE Computing." : 'Your free account is ready. Start revising now!'}
                </p>
                <div className="bg-emerald-700 text-white font-semibold px-6 py-2.5 rounded-lg text-sm whitespace-nowrap flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  {activeTab === 'login' ? 'Welcome Back!' : 'Redirecting...'}
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center bg-slate-100 rounded-full p-1 gap-1 mb-8 w-fit">
                  {(['login', 'register'] as const).map((tab) => (
                    <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${activeTab === tab ? 'bg-white text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>
                      {tab === 'login' ? 'Log In' : 'Register Free'}
                    </button>
                  ))}
                </div>

                {activeTab === 'login' ? (
                  <form onSubmit={handleLogin} className="flex flex-col gap-5 max-w-md">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Welcome back</h2>
                      <p className="text-slate-500 text-sm">Log in to access your revision dashboard.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                      <input type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="your@email.com" className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-sm font-medium text-slate-700">Password</label>
                        <a href="#" className="text-xs text-emerald-700 hover:text-emerald-800 cursor-pointer">Forgot password?</a>
                      </div>
                      <div className="relative">
                        <input type={showPass ? 'text' : 'password'} required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="••••••••" className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors pr-10" />
                        <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 cursor-pointer">
                          <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                        </button>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={loginForm.remember} onChange={(e) => setLoginForm({ ...loginForm, remember: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
                      <span className="text-sm text-slate-600">Remember me</span>
                    </label>
                    <button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm">Log In</button>
                    <p className="text-sm text-slate-500 text-center">Don&apos;t have an account? <button type="button" onClick={() => setActiveTab('register')} className="text-emerald-700 font-semibold cursor-pointer">Register free</button></p>
                  </form>
                ) : (
                  <form onSubmit={handleRegister} className="flex flex-col gap-4 max-w-md">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Create your account</h2>
                      <p className="text-slate-500 text-sm">Free forever. No credit card required.</p>
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Account type</label>
                        <div className="flex items-center bg-slate-100 rounded-full p-1 gap-1 w-fit">
                          {(['student', 'tutor'] as const).map((r) => (
                            <button
                              key={r}
                              type="button"
                              onClick={() => setRegForm({ ...regForm, role: r })}
                              className={`px-4 py-1 rounded-full text-sm font-semibold transition-all ${regForm.role === r ? 'bg-white text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                              {r.charAt(0).toUpperCase() + r.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name</label>
                        <input type="text" required value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} placeholder="Your name" className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number</label>
                        <input
                          type="tel"
                          value={regForm.phone}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val.startsWith('+44 ')) {
                              setRegForm({ ...regForm, phone: val });
                            } else if (val.length < 4) {
                              setRegForm({ ...regForm, phone: '+44 ' });
                            }
                          }}
                          placeholder="+44 7700 000000"
                          maxLength={15}
                          className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                      <input type="email" required value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} placeholder="your@email.com" className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors" />
                    </div>
                    {regForm.role === 'student' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">School / College</label>
                          <input type="text" value={regForm.school} onChange={(e) => setRegForm({ ...regForm, school: e.target.value })} placeholder="e.g. Oakwood Academy" className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">Year Group</label>
                          <select value={regForm.yearGroup} onChange={(e) => setRegForm({ ...regForm, yearGroup: e.target.value })} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors bg-white">
                            <option value="">Select year</option>
                            <option>Year 10</option>
                            <option>Year 11</option>
                            <option>Year 12</option>
                            <option>Year 13</option>
                            <option>Other</option>
                          </select>
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                      <div className="relative">
                        <input type={showPass ? 'text' : 'password'} required value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} placeholder="Min. 8 characters" className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors pr-10" />
                        <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 cursor-pointer">
                          <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                        </button>
                      </div>
                    </div>
                    <button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm mt-1">Create Free Account</button>
                    <p className="text-xs text-slate-400 text-center">By registering you agree to our <a href="#" className="text-emerald-700 cursor-pointer">Terms</a> and <a href="#" className="text-emerald-700 cursor-pointer">Privacy Policy</a>.</p>
                    <p className="text-sm text-slate-500 text-center">Already have an account? <button type="button" onClick={() => setActiveTab('login')} className="text-emerald-700 font-semibold cursor-pointer">Log in</button></p>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Toast Notification */}
      <div
        className={`fixed bottom-6 right-6 z-[60] transition-all duration-500 transform ${toast.show ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0 pointer-events-none'
          }`}
      >
        <div className={`flex items-center gap-4 px-5 py-4 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] backdrop-blur-md border ${toast.type === 'success'
            ? 'bg-emerald-600/95 border-emerald-500/50'
            : 'bg-rose-600/95 border-rose-500/50'
          } text-white min-w-[320px]`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${toast.type === 'success' ? 'bg-white/20' : 'bg-white/20'
            }`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-2xl`}></i>
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-sm leading-tight">
              {toast.type === 'success' ? 'Success' : 'Error'}
            </h4>
            <p className="text-white/80 text-xs mt-0.5 leading-relaxed">{toast.message}</p>
          </div>
          <button
            onClick={() => setToast(prev => ({ ...prev, show: false }))}
            className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
